import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

function ItemList({ endpoint, label }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () =>
    fetch(baseUrl + endpoint, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setItems(Array.isArray(d) ? d : []));

  useEffect(() => { load(); }, [endpoint]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    await fetch(baseUrl + endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ name: newName.trim() }),
    }).then(r => r.json()).then(d => { if (Array.isArray(d)) setItems(d); });
    setNewName('');
    setSaving(false);
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    await fetch(baseUrl + endpoint + '/' + id, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ name: editName.trim() }),
    }).then(r => r.json()).then(d => { if (Array.isArray(d)) setItems(d); });
    setEditId(null);
    setEditName('');
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    await fetch(baseUrl + endpoint + '/' + id, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(r => r.json()).then(d => { if (Array.isArray(d)) setItems(d); });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
        <TextField
          size="small"
          label={`New ${label}`}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          sx={{ flex: 1 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={saving || !newName.trim()}>
          Add
        </Button>
      </Box>
      <List dense>
        {items.map(item => (
          <ListItem
            key={item.id}
            secondaryAction={
              editId === item.id ? (
                <>
                  <IconButton size="small" onClick={() => handleEdit(item.id)} disabled={saving}><CheckIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => setEditId(null)}><CloseIcon fontSize="small" /></IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small" onClick={() => { setEditId(item.id); setEditName(item.name); }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                </>
              )
            }
          >
            {editId === item.id ? (
              <TextField
                size="small"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEdit(item.id)}
                autoFocus
                sx={{ mr: 1, flex: 1 }}
              />
            ) : (
              <ListItemText primary={item.name} />
            )}
          </ListItem>
        ))}
        {items.length === 0 && (
          <ListItem><ListItemText secondary={`No ${label.toLowerCase()}s yet`} /></ListItem>
        )}
      </List>
    </Box>
  );
}

export default function ManageReferenceData({ open, onClose }) {
  const [tab, setTab] = useState(0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Reference Data</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Work Types" />
          <Tab label="Logistics Types" />
        </Tabs>
        {tab === 0 && <ItemList endpoint="schedule/work-types" label="Work Type" />}
        {tab === 1 && <ItemList endpoint="schedule/logistics-types" label="Logistics Type" />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
